import { useGetDeviationAnalysis } from 'ClientPortal/src/api/Queries/value-overview'
import { getLeadingValuesPerSqmDeviationAnalysis, getPerAreaLabels, ValuationDeviationOptions } from 'ComponentLibrary'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { throwOnForbiddenApiError } from 'ClientPortal/src/api/react-query-config'
import type { CustomerExistingValuation as ExistingValuation } from '@einwert/valuation-api-client'
import { useTranslation } from 'react-i18next'

type DeviationAnalysisProps = {
    valuationOptions?: ValuationDeviationOptions
    lockedBaseValuation?: boolean
    selectedValuation: ExistingValuation | undefined
}

export function useDeviationAnalysis({ valuationOptions, selectedValuation, lockedBaseValuation }: DeviationAnalysisProps) {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const [baseValuationId, setBaseValuationId] = useState<string | undefined>(selectedValuation?.uuid)
    const [comparisonValuationId, setComparisonValuationId] = useState(
        getDefaultComparisonValuationId({ valuationOptions, selectedValuation })
    )

    useEffect(() => {
        if (lockedBaseValuation && selectedValuation) {
            setBaseValuationId(selectedValuation.uuid)
            setComparisonValuationId(getDefaultComparisonValuationId({ valuationOptions, selectedValuation }))
        }
    }, [lockedBaseValuation, selectedValuation, valuationOptions])

    const { data: deviationAnalysis, isPending: isDeviationAnalysisPending } = useGetDeviationAnalysis({
        baseValuationId,
        comparisonValuationId,
        throwOnError: throwOnForbiddenApiError,
    })

    const baseValuation = valuationOptions?.find((option) => option.value === baseValuationId)
    const comparisonValuation = valuationOptions?.find((option) => option.value === comparisonValuationId)
    const perAreaLabels = getPerAreaLabels(tCommon)

    return {
        deviationAnalysis: deviationAnalysis && {
            ...deviationAnalysis,
            leading_market_value_per_area: getLeadingValuesPerSqmDeviationAnalysis(deviationAnalysis, perAreaLabels),
        },
        isDeviationAnalysisPending,
        setBaseValuationId,
        setComparisonValuationId,
        baseValuation,
        comparisonValuation,
    }
}

const getDefaultComparisonValuationId = ({
    valuationOptions,
    selectedValuation,
}: {
    valuationOptions?: ValuationDeviationOptions
    selectedValuation?: ExistingValuation
}) => {
    const selectedValuationIdIndex = valuationOptions?.findIndex((item) => item.value === selectedValuation?.uuid)
    if (selectedValuation?.deviation_valuation_uuid) {
        return selectedValuation.deviation_valuation_uuid
    }
    if (selectedValuationIdIndex !== undefined && valuationOptions !== undefined && selectedValuationIdIndex !== -1) {
        if (selectedValuationIdIndex === valuationOptions.length - 1) {
            return selectedValuation?.uuid
        }
        for (let i = selectedValuationIdIndex + 1; i < valuationOptions.length; i++) {
            if (
                dayjs(valuationOptions[i].valuationDate).isBefore(dayjs(valuationOptions[selectedValuationIdIndex].valuationDate)) &&
                !valuationOptions[i].isDraft
            ) {
                return valuationOptions[i].value
            }
        }
        return valuationOptions[selectedValuationIdIndex + 1].value
    }
    return undefined
}
